<script>
  import { getTotalGuestFormatting } from '../../../../components/availability/availability-utilities';
  import { clickOutside } from '../action/clickOutside.js';
  import {
    availabilityData,
    availabilityLocationTypes,
    fetchStates,
    isFetchingAvailability,
  } from './../store/fetchAvailabilityData.js';
  import {
    availabilityEnablesDatesData,
    getAvailabilityEnableDates,
  } from './../store/fetchAvailabilityEnableDates.js';
  import {
    adultsValue,
    childrenValue,
    guestsQuery,
    infantsValue,
    numberOfGuest,
    scContextItemIdQuery,
    scFormTokenQuery,
  } from './../store/store.js';
  import {
    getUrlParameter,
    returnNumberOrZero,
    upperCaseFirstLetter,
  } from './../util.js';
  import ClearButton from './ClearButton.svelte';
  import GuestInput from './GuestInput.svelte';

  const adultsLabel = 'adults';
  const childrenLabel = 'children';
  const infantsLabel = 'infants';
  const urlLocationSearch = window.location.search;
  const adultsValueParam = parseInt(
    getUrlParameter(urlLocationSearch, adultsLabel)
  );
  const childrenValueParam = parseInt(
    getUrlParameter(urlLocationSearch, childrenLabel)
  );
  const infantsValueParam = parseInt(
    getUrlParameter(urlLocationSearch, infantsLabel)
  );
  const adultsStoredValue = returnNumberOrZero(
    localStorage.getItem(adultsLabel)
  );
  const childrenStoredValue = returnNumberOrZero(
    localStorage.getItem(childrenLabel)
  );
  const infantsStoredValue = returnNumberOrZero(
    localStorage.getItem(infantsLabel)
  );
  let hidden = true;
  let toggleButtonElement;

  $: if (
    adultsValueParam >= 0 &&
    childrenValueParam >= 0 &&
    infantsValueParam >= 0
  ) {
    adultsValue.set(adultsValueParam);
    childrenValue.set(childrenValueParam);
    infantsValue.set(infantsValueParam);
  } else if (adultsStoredValue || childrenStoredValue || infantsStoredValue) {
    adultsValue.set(adultsStoredValue);
    childrenValue.set(childrenStoredValue);
    infantsValue.set(infantsStoredValue);
  }

  $: isGuestLessThanTwo = $numberOfGuest <= 1;
  $: isGuestZero = $numberOfGuest === 0;

  const toggleVisibility = () => (hidden = !hidden);

  const updateInputValue = (event, storeValue) => {
    storeValue.set(event.detail.count);
    getAvailabilityEnableDates(
      $scContextItemIdQuery + $guestsQuery + '&' + $scFormTokenQuery
    );
    setValuesToLocalStorages();
  };

  const setValuesToLocalStorages = () => {
    localStorage.setItem(adultsLabel, $adultsValue);
    localStorage.setItem(childrenLabel, $childrenValue);
    localStorage.setItem(infantsLabel, $infantsValue);
  };

  const clearGuest = () => {
    adultsValue.set(1);
    childrenValue.set(0);
    infantsValue.set(0);
    availabilityEnablesDatesData.set({});
    isFetchingAvailability.set(fetchStates.IDLE);
    availabilityLocationTypes.set([]);
    availabilityData.set({});
  };
</script>

<svelte:window
  on:load={getAvailabilityEnableDates(
    $scContextItemIdQuery + $guestsQuery + '&' + $scFormTokenQuery
  )} />

<div
  class="bg-white border rounded-1 border-gray-dark box-sizing-border cursor-pointer d-flex f4 flex-1 flex-items-center flex-justify-between poppins-font is-active lh-condensed position-relative text-left width-full"
  tabindex="0">
  <svg
    class="left-0 top-0 m-2 opacity-50 position-absolute z-index-1"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 19"
    width="20"
    height="20"
    aria-hidden="true"
    focusable="false"
    ><path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M3.496 5.499a5.5 5.5 0 118.596 4.547 9.005 9.005 0 015.9 8.18.749.749 0 01-.727.772H9.089s-7.392-.016-8.361-.016A.75.75 0 010 18.227a9.005 9.005 0 015.9-8.181 5.495 5.495 0 01-2.404-4.547z"
      fill="#2B2B2B" /><path
      d="M19 4.504a4.23 4.23 0 00-2.119-.502c-.514.014-.381.502-.381.502s.224 2.579-.5 3.996c-.299.585-1 1.5-1 1.5s1.957 1.18 3 2.5c1.628 2.062 1.994 4.16 2 6.5h3.434a.58.58 0 00.528-.382.6.6 0 00.038-.228 7.178 7.178 0 00-1.321-3.943 7.018 7.018 0 00-3.27-2.515 4.324 4.324 0 001.428-1.667 4.397 4.397 0 00-.234-4.266 4.304 4.304 0 00-1.602-1.495z"
      fill="#2B2B2B" /></svg>
  <button
    id="availability-guestpicker"
    type="button"
    class="bg-transparent border-0 cursor-pointer outline-focus col-6 d-flex f4 flex-items-center lh-condensed p-2 pl-5 text-left width-full"
    aria-controls="availability-guest-picker"
    aria-expanded={hidden}
    data-testid="availability-guestpicker-toggle"
    bind:this={toggleButtonElement}
    on:click={toggleVisibility}>
    <span
      class="poppins-font no-wrap"
      class:text-gray={isGuestZero}
      id="availability-guestpicker-total"
      data-behavior="availability-guestpicker-total"
      data-testid="availability-guestpicker-total">
      {getTotalGuestFormatting($numberOfGuest)}
    </span>
    <span class="sr-only">Open guest picker</span>
  </button>
  <ClearButton
    label="guest"
    hidden={isGuestLessThanTwo}
    buttonId="guest-button"
    on:click={clearGuest} />
</div>
{#if !hidden}
  <div
    id="availability-guestpicker"
    class="Box anim-fade-down border box-shadow-large box-sizing-border left-0 mt-1 position-absolute right-0 rounded-1 width-md-auto z-index-100"
    data-testid="availability-guestpicker"
    use:clickOutside={toggleButtonElement}
    on:click-outside={toggleVisibility}>
    <div class="p-3 text-left">
      <GuestInput
        label={upperCaseFirstLetter(adultsLabel)}
        defaultCount={$adultsValue}
        minValue={0}
        on:number-of-guest={(event) => updateInputValue(event, adultsValue)} />

      <GuestInput
        label={upperCaseFirstLetter(childrenLabel)}
        labelHint="5–15 yrs"
        defaultCount={$childrenValue}
        minValue={0}
        on:number-of-guest={(event) =>
          updateInputValue(event, childrenValue)} />

      <GuestInput
        label={upperCaseFirstLetter(infantsLabel)}
        labelHint="0–4 yrs"
        defaultCount={$infantsValue}
        minValue={0}
        on:number-of-guest={(event) => updateInputValue(event, infantsValue)} />

      <button
        type="button"
        class="bttn bttn-invisible d-block f4 poppins-bold-font ml-auto mr-n3"
        data-testid="availability-guestpicker-apply-button"
        on:click={toggleVisibility}>Apply</button>
    </div>
  </div>
{/if}
